import Component from '@ember/component';
import {
  action,
  computed,
  get,
  getProperties,
  set,
  setProperties,
} from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isBlank, isEmpty, isEqual } from '@ember/utils';
import EditLineItemSelectedContactComponent from './edit-line-item-selected-contact';

import moment from 'moment';

// arguments
// - line
// - changeset
// - campaignLineItemChangeset
// - transTypes
// - sortedTransTypes
// - lastGiftForContact
// - focusOn
//   - expected format: { line: {line}, fieldName: {fieldName} }
// - showValidatonErrors
// actions
// - onDelete
export default class extends Component {
  tagName = 'tr';
  classNameBindings = ['showHasErrorsBackground:table-warning'];

  @service campaignService;
  @service contactsCache;
  @service editTransactionsService;
  @service fundsService;
  @service store;
  @service storeCache;

  showValidationErrors = null;
  @alias('contactsCache.sortedContacts') contacts;

  get campaignSelectOptions() {
    return [
      { type: 'none', name: 'None' },
      ...this.campaignService.sortedNonRestrictedCampaigns,
    ];
  }

  get selectedItemComponent() {
    return EditLineItemSelectedContactComponent;
  }

  get showCampaignDropDown() {
    return (
      this.campaignService.campaignsEnabled &&
      this.campaignService.sortedNonRestrictedCampaigns.length > 0
    );
  }

  @computed('changeset.contactId', 'newContact')
  get selectedContact() {
    let contactId = get(this, 'changeset.contactId');
    if (contactId != null) {
      return this.contactsCache.contactsById[contactId];
    }
    if (this.newContact != null) {
      return this.newContact;
    }
    return null;
  }

  @computed('campaignLineItemChangeset.campaignId')
  get selectedCampaign() {
    let campaignId = get(this, 'campaignLineItemChangeset.campaignId');
    if (campaignId != null) {
      return this.campaignService.campaignsById[campaignId];
    }
    return { type: 'none', name: 'None' };
  }

  // If the user created a new contact, this property is set to the new contact record
  newContact = null;
  @computed('contacts.[]')
  get initialContacts() {
    let initialContacts = this.contacts.slice(0, 20);
    if (this.contacts.length > initialContacts.length) {
      initialContacts.push({
        type: 'more',
      });
    }
    return initialContacts;
  }

  activePledgeForSelectedContact = null;
  // should be called when selectedContact or date are changed
  getActivePledgeForSelectedContact() {
    let transDate = get(this.changeset, 'transDate');
    if (isEmpty(transDate)) {
      return;
    }
    let formattedTransDate = moment(transDate).format('YYYY-MM-DD');

    let selectedContact = this.selectedContact;
    if (selectedContact) {
      this.fundsService.confirmValidFundToken().then(() => {
        this.store
          .query('pledge', {
            filter: {
              contact_id: selectedContact.id,
              start_date: formattedTransDate,
              end_date: formattedTransDate,
            },
          })
          .then((records) => {
            let firstPledge = records.firstObject;
            set(this, 'activePledgeForSelectedContact', firstPledge);
          });
      });
    } else {
      set(this, 'activePledgeForSelectedContact', null);
    }
  }

  transTypes = null; // passed in
  sortedTransTypes = null; // passed in
  addressFieldsVisible = false;

  @computed(
    'changeset.errors.length',
    'model.errors.length',
    'showValidationErrors',
  )
  get showHasErrorsBackground() {
    if (this.showValidationErrors === false) {
      return false;
    }
    return (
      get(this, 'changeset.errors.length') > 0 ||
      get(this, 'model.errors.length') > 0
    );
  }

  didReceiveAttrs() {
    this._super(...arguments);
    let { changeset, focusOn } = getProperties(this, 'changeset', 'focusOn');
    if (focusOn.changeset === changeset) {
      this.focusOnField(focusOn.fieldName);
    }
    this.getActivePledgeForSelectedContact();
  }

  // The field name that will have focus after the current run loop completes
  focusingOnField = null;
  focusComesFromOutside(e) {
    let blurredEl = e.relatedTarget;
    return (
      !blurredEl ||
      !blurredEl.classList.contains('ember-power-select-search-input')
    );
  }

  @action
  showAddressFields() {
    set(this, 'addressFieldsVisible', true);
  }

  @action
  handleAddressFieldsSaved() {
    // Since the contact was saved, we no longer need to show the link to
    // edit the address fields
    setProperties(this, {
      newContact: null,
      addressFieldsVisible: false,
    });
  }

  @action
  handleAddressFieldsCancel() {
    set(this, 'addressFieldsVisible', false);
  }

  @action
  powerSelectFocused(select, e) {
    if (!e.target.classList.contains('ember-power-select-trigger')) {
      return;
    }
    let focusingOnField = this.focusingOnField;
    if (
      focusingOnField != null &&
      !e.target.classList.contains(`field-${focusingOnField}`)
    ) {
      return;
    }
    if (this.focusComesFromOutside(e)) {
      setTimeout(() => {
        select.actions.open();
      }, 0);
    }
  }

  // TODO: Search on multiple words
  @action
  searchContacts(term) {
    if (term == null) {
      return this.contacts.slice(0, 20);
    }
    let termLowerCase = term.trim().toLowerCase();
    let filteredContacts = this.contacts.filter((contact) => {
      return contact.nameLowerCase.indexOf(termLowerCase) >= 0;
    });

    // If there isn't an exact match, then give the option to create a new contact
    if (
      filteredContacts.find((contact) => {
        return contact.nameLowerCase === termLowerCase;
      }) == null
    ) {
      filteredContacts.push({
        type: 'create',
        name: `Create '${term}' as a new contact`,
      });
    }

    return filteredContacts;
  }

  @action
  selectContact(contact) {
    if (contact.type === 'more') {
      // do nothing
    } else if (contact.type === 'create') {
      let name = contact.name.match(/^Create '(.*)' as a new contact$/)[1];
      let newContact = this.store.createRecord('contact', {
        name: name,
        active: true,
      });
      set(this, 'newContact', newContact);
      newContact.save().then(() => {
        set(this.contactsCache, 'contacts', [
          ...this.contactsCache.contacts,
          newContact,
        ]);
        set(this, 'changeset.contactId', newContact.id);
        this.getActivePledgeForSelectedContact();
      });
    } else {
      // Automatically set amount to the last amount the contact gave
      if (isBlank(get(this, 'changeset.contactId'))) {
        let lastGift = get(this, `lastGiftForContact.${contact.id}`);
        if (lastGift != null) {
          set(this, 'changeset.amount', get(lastGift, 'amount'));
          set(this, 'changeset.transType', get(lastGift, 'transType'));
        }
      }
      set(this, 'changeset.contactId', contact.id);
      set(this, 'activePledgeForSelectedContact', null);
      this.getActivePledgeForSelectedContact();
    }

    if (this.showCampaignDropDown) {
      this.focusOnField('campaign');
    } else {
      this.focusOnField('transType');
    }
  }

  @action
  selectCampaign(campaign) {
    if (campaign.type === 'none') {
      set(this, 'campaignLineItemChangeset.campaignId', null);
    } else {
      set(this, 'campaignLineItemChangeset.campaignId', campaign.id);
    }

    this.focusOnField('transType');
  }

  @action
  searchCampaigns(term) {
    if (term == null) {
      return this.campaignSelectOptions;
    }
    let termLowerCase = term.trim().toLowerCase();
    let filteredCampaigns = this.campaignSelectOptions.filter((campaign) => {
      return campaign.name.toLowerCase().indexOf(termLowerCase) >= 0;
    });

    return filteredCampaigns;
  }

  @action
  searchTransTypes(term) {
    let transTypes = this.sortedTransTypes;
    if (isEmpty(term)) {
      return transTypes;
    } else {
      let lowerCaseTerm = term.toLowerCase();
      let filtered = transTypes.filter((item) => {
        return item.toLowerCase().indexOf(lowerCaseTerm) > -1;
      });
      let exactMatch = transTypes.find((item) => {
        return item.toLowerCase() === lowerCaseTerm;
      });
      if (exactMatch == null) {
        filtered.push(`Create '${term}' Type`);
      }
      return filtered;
    }
  }

  @action
  buildSelectionSameOption(focusOnField, option, select) {
    if (isEqual(option, select.selected)) {
      this.focusOnField(focusOnField, { selectText: true });
    }
    return option;
  }

  @action
  selectTransType(transType) {
    let createTypeMatch = transType.match(/^Create '(.*)' Type$/);
    if (createTypeMatch != null) {
      let newTransType = createTypeMatch[1];
      this.transTypes = [...this.transTypes, newTransType];
      transType = newTransType;
    }
    set(this, 'changeset.transType', transType);
    this.focusOnField('amount', { selectText: true });
  }

  // options
  // - selectText => if true, then it selects the text too;
  @action
  focusOnField(fieldName, { selectText } = {}) {
    // can't use scheduleOnce('afterRender') because pikaday isn't done initializing yet
    set(this, 'focusingOnField', fieldName);
    setTimeout(() => {
      setTimeout(() => {
        let field = this.element.querySelector(`.field-${fieldName}`);
        if (field) {
          field.focus();
          if (selectText) {
            field.select();
          }
        }
        set(this, 'focusingOnField', null);
      }, 150);
    }, 0);
  }

  @action
  validateField(fieldName) {
    this.changeset.validate(fieldName);
  }

  @action
  selectTransDate(date) {
    if (date) {
      set(this, 'changeset.transDate', date);
      this.getActivePledgeForSelectedContact();
    }
  }

  @action
  onPikadayOpened() {
    set(this, 'pikadayOpenedDate', get(this, 'changeset.transDate'));
  }

  @action
  onPikadayClosed() {
    let currentDate = get(this, 'changeset.transDate');
    if (currentDate !== this.pikadayOpenedDate) {
      this.focusOnField('contactId');
    }
  }

  @action
  delete(changeset) {
    this.onDelete(changeset);
  }
}
